// src/admin/pages/PlanModal.jsx — create/edit a credit plan (superadmin only).
//
// The plan model is no longer { name, credits, active }. What an operator sets
// here decides what a tenant may generate, how many seats it gets and when its
// allowance resets, so the form is grouped by consequence rather than by field:
//
//   • Allowance   — `monthly_product_credits` under `enforces_limit`. This is
//                   the number the enforcement reads (100 / 150 / 250), and it
//                   is MANDATORY whenever enforcement is on (the BE 422s
//                   otherwise). On an unlimited plan a null allowance is
//                   legitimate, so the field disappears with the toggle.
//   • Legacy cost — `monthly_credits`. Kept ONLY so pre-change periods stay
//                   explicable; it is not what a customer sees on /balance.
//                   Labelled as legacy and folded away by default.
//   • Window      — `period_days` + `renews`, shown together because neither
//                   means anything alone (see the live consequence line).
//   • Formats     — `allowed_formats` tokens. "All formats" is `null`, NEVER
//                   `[]` (an empty list 422s and would sell nothing).
//   • Caps        — `max_users` (seats per workspace) vs `max_tenants`
//                   (workspaces per licence). Two different entitlements; they
//                   live in separate boxes on purpose.
//
// PATCH semantics: an omitted or null field means "unchanged", and
// `period_days` / `allowed_formats` / `max_users` cannot be cleared once set.
// This form therefore offers NO control that claims to remove them — the
// affordance is disabled with an explanation instead of failing silently.
//
// ⛔⛔ KNOWN BROKEN, deliberately left so: the backend withdrew `allowed_formats`
// from its plan model, which is `extra="forbid"`, so every create AND edit sent
// from here answers 422. There is no FE way to change what a plan grants; the
// replacement API is GET/PUT /api/v1/admin/plan-features/{grant_key}, and plans
// are maintained with the backend CLI. Do not "fix" this opportunistically.
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AdminModal from '../components/AdminModal.jsx';
import { adminCreatePlan, adminUpdatePlan } from '../adminApi.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
  color: COLORS.ink, fontSize: '14px', boxSizing: 'border-box',
  outline: 'none', fontFamily: FONT_SANS,
};
const labelStyle = { display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px', fontWeight: 500 };
const helpStyle = { display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginTop: '6px', lineHeight: 1.45 };
const fieldsetStyle = {
  border: `1px solid ${COLORS.border}`, borderRadius: RADII.controlInner,
  padding: '14px 16px', marginBottom: '16px', minWidth: 0,
};
const legendStyle = { color: COLORS.ink, fontSize: '13px', fontWeight: 600, padding: '0 6px' };
const checkboxRow = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: COLORS.ink, cursor: 'pointer' };
const noticeStyle = {
  marginTop: '8px', padding: '8px 10px', borderRadius: RADII.controlInner,
  background: SEMANTIC.neutro.bg, color: SEMANTIC.neutro.fg, fontSize: '12px', lineHeight: 1.45,
};

// `platform`, `platform:format` or `platform:*`. Lower-case, no spaces.
const TOKEN_RE = /^[a-z0-9][a-z0-9._-]*(:([a-z0-9][a-z0-9._-]*|\*))?$/;
// Plan ids are slugs (they are the primary key, not a generated uuid).
const PLAN_ID_RE = /^[a-z0-9][a-z0-9_-]*$/;

const asIntString = (v) => (v == null ? '' : String(v));

export default function PlanModal({ open, plan, onClose, onSaved }) {
  const { t } = useTranslation('admin');
  const isEdit = !!plan;

  const [planId, setPlanId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [enforcesLimit, setEnforcesLimit] = useState(true);
  const [productCredits, setProductCredits] = useState('');
  const [monthlyCredits, setMonthlyCredits] = useState('');
  const [showLegacy, setShowLegacy] = useState(false);
  const [periodDays, setPeriodDays] = useState('');
  const [renews, setRenews] = useState(true);
  const [selfServe, setSelfServe] = useState(false);
  const [maxTenants, setMaxTenants] = useState('');
  const [maxUsers, setMaxUsers] = useState('');
  const [formatsMode, setFormatsMode] = useState('all'); // 'all' | 'restricted'
  const [formats, setFormats] = useState([]);
  const [formatDraft, setFormatDraft] = useState('');
  const [formatError, setFormatError] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // A field the BE already holds a value for cannot be cleared by PATCH.
  const lockedFormats = isEdit && Array.isArray(plan?.allowed_formats) && plan.allowed_formats.length > 0;
  const lockedPeriod = isEdit && plan?.period_days != null;
  const lockedSeats = isEdit && plan?.max_users != null;
  // Same reasoning for max_tenants: PATCH reads null as "unchanged", so a
  // blanked value would be a control that silently does nothing.
  const lockedTenants = isEdit && plan?.max_tenants != null;

  // The allowance is mandatory whenever enforcement is on — but NOT when we are
  // editing a plan an older backend served without the field at all. Demanding
  // a number the server never had would block an unrelated rename; omitting it
  // from the PATCH leaves it exactly as it was.
  const allowanceRequired = enforcesLimit && (
    !isEdit || plan?.monthly_product_credits != null || plan?.enforces_limit === false
  );

  // A boolean the loaded plan never carried is only sent if the operator
  // actually touched it — otherwise a PATCH would write our local default over
  // a field the server may hold differently.
  const sendBool = (key, value, fallback) => (plan?.[key] !== undefined || value !== fallback ? value : undefined);

  useEffect(() => {
    if (!open) return;
    setError('');
    setFormatError('');
    setFormatDraft('');
    setPlanId(plan?.id || '');
    setDisplayName(plan?.display_name || '');
    // A missing `enforces_limit` (old backend) reads as "enforced" — the same
    // default the BE applies — rather than silently offering an unlimited plan.
    setEnforcesLimit(plan ? plan.enforces_limit !== false : true);
    setProductCredits(asIntString(plan?.monthly_product_credits));
    setMonthlyCredits(asIntString(plan?.monthly_credits));
    setShowLegacy(false);
    setPeriodDays(asIntString(plan?.period_days));
    setRenews(plan ? plan.renews !== false : true);
    setSelfServe(plan ? plan.self_serve === true : false);
    setMaxTenants(asIntString(plan?.max_tenants));
    setMaxUsers(asIntString(plan?.max_users));
    const allowed = Array.isArray(plan?.allowed_formats) ? plan.allowed_formats : null;
    setFormatsMode(allowed && allowed.length ? 'restricted' : 'all');
    setFormats(allowed ? [...allowed] : []);
    setActive(plan?.active ?? true);
  }, [open, plan]);

  // period_days + renews only mean something together — spell the outcome out.
  const windowConsequence = useMemo(() => {
    if (periodDays === '' || periodDays == null) return t('planModal.window.calendarMonth');
    return renews ? t('planModal.window.rolling', { n: periodDays }) : t('planModal.window.trial', { n: periodDays });
  }, [periodDays, renews, t]);

  function addFormatToken(raw) {
    const token = String(raw || '').trim().toLowerCase();
    if (!token) return;
    if (!TOKEN_RE.test(token)) {
      setFormatError(t('planModal.formats.invalidToken', { token }));
      return;
    }
    setFormatError('');
    setFormats((prev) => (prev.includes(token) ? prev : [...prev, token]));
    setFormatDraft('');
  }

  function handleFormatKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addFormatToken(formatDraft);
    }
  }

  function validate() {
    if (!isEdit && !PLAN_ID_RE.test(planId.trim())) return t('planModal.errors.idInvalid');
    if (allowanceRequired) {
      const n = Number(productCredits);
      if (productCredits === '' || !Number.isFinite(n) || n < 0) return t('planModal.errors.allowanceRequired');
    }
    if (formatsMode === 'restricted' && formats.length === 0) return t('planModal.errors.formatsEmpty');
    for (const [value, min, key] of [
      [periodDays, 1, 'periodDays'],
      [maxTenants, 1, 'maxTenants'],
      [maxUsers, 1, 'maxUsers'],
    ]) {
      if (value === '') continue;
      const n = Number(value);
      if (!Number.isInteger(n) || n < min) return t('planModal.errors.minInteger', { field: t(`planModal.${key}.label`), min });
    }
    if (lockedPeriod && periodDays === '') return t('planModal.errors.notClearable', { field: t('planModal.periodDays.label') });
    if (lockedSeats && maxUsers === '') return t('planModal.errors.notClearable', { field: t('planModal.maxUsers.label') });
    if (lockedTenants && maxTenants === '') return t('planModal.errors.notClearable', { field: t('planModal.maxTenants.label') });
    return '';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const invalid = validate();
    if (invalid) { setError(invalid); return; }
    setError('');
    setLoading(true);
    // "All formats" is null; a restricted list is never empty (validate() blocks it).
    const allowedFormats = formatsMode === 'restricted' ? formats : null;
    try {
      if (isEdit) {
        await adminUpdatePlan(plan.id, {
          display_name: displayName,
          monthly_credits: monthlyCredits === '' ? undefined : monthlyCredits,
          monthly_product_credits: enforcesLimit && productCredits !== '' ? productCredits : undefined,
          enforces_limit: sendBool('enforces_limit', enforcesLimit, true),
          renews: sendBool('renews', renews, true),
          self_serve: sendBool('self_serve', selfServe, false),
          period_days: periodDays === '' ? null : periodDays,
          max_tenants: maxTenants === '' ? null : maxTenants,
          max_users: maxUsers === '' ? null : maxUsers,
          allowed_formats: allowedFormats,
          active,
        });
      } else {
        await adminCreatePlan({
          id: planId.trim(),
          display_name: displayName,
          monthly_credits: monthlyCredits === '' ? 0 : monthlyCredits,
          monthly_product_credits: enforcesLimit ? productCredits : undefined,
          enforces_limit: enforcesLimit,
          renews,
          self_serve: selfServe,
          period_days: periodDays,
          max_tenants: maxTenants,
          max_users: maxUsers,
          allowed_formats: allowedFormats,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AdminModal open={open} onClose={onClose} title={isEdit ? t('planModal.editTitle') : t('planModal.createTitle')}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="plan-id" style={labelStyle}>{t('planModal.planId.label')}</label>
          <input
            id="plan-id" type="text" value={planId} readOnly={isEdit} disabled={isEdit}
            onChange={e => setPlanId(e.target.value)} required
            style={{ ...inputStyle, background: isEdit ? COLORS.bg : COLORS.surface }}
          />
          <span style={helpStyle}>{isEdit ? t('planModal.planId.fixed') : t('planModal.planId.help')}</span>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="plan-name" style={labelStyle}>{t('planModal.name')}</label>
          <input id="plan-name" type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} required style={inputStyle} />
        </div>

        {/* ── Allowance ─────────────────────────────────────────────── */}
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{t('planModal.allowance.legend')}</legend>
          <label style={{ ...checkboxRow, marginBottom: '10px' }}>
            <input type="checkbox" checked={enforcesLimit} onChange={e => setEnforcesLimit(e.target.checked)} />
            {t('planModal.enforcesLimit.label')}
          </label>
          {enforcesLimit ? (
            <div>
              <label htmlFor="plan-product-credits" style={labelStyle}>{t('planModal.productCredits.label')}</label>
              <input
                id="plan-product-credits" type="number" min="0" step="1" value={productCredits}
                onChange={e => setProductCredits(e.target.value)} required={allowanceRequired}
                aria-describedby="plan-product-credits-help" style={inputStyle}
              />
              <span id="plan-product-credits-help" style={helpStyle}>{t('planModal.productCredits.help')}</span>
            </div>
          ) : (
            <div style={noticeStyle}>{t('planModal.productCredits.unlimited')}</div>
          )}
        </fieldset>

        {/* ── Billing window ────────────────────────────────────────── */}
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{t('planModal.window.legend')}</legend>
          <label htmlFor="plan-period-days" style={labelStyle}>{t('planModal.periodDays.label')}</label>
          <input
            id="plan-period-days" type="number" min="1" step="1" value={periodDays}
            onChange={e => setPeriodDays(e.target.value)}
            placeholder={t('planModal.periodDays.placeholder')} style={inputStyle}
          />
          <span style={helpStyle}>{t('planModal.periodDays.help')}</span>
          <label style={{ ...checkboxRow, marginTop: '12px' }}>
            <input type="checkbox" checked={renews} onChange={e => setRenews(e.target.checked)} />
            {t('planModal.renews.label')}
          </label>
          <div style={noticeStyle}>{windowConsequence}</div>
          {lockedPeriod && <div style={noticeStyle}>{t('planModal.notClearable', { field: t('planModal.periodDays.label') })}</div>}
        </fieldset>

        {/* ── Allowed formats ───────────────────────────────────────── */}
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{t('planModal.formats.legend')}</legend>
          <label style={{ ...checkboxRow, marginBottom: '6px', opacity: lockedFormats ? 0.6 : 1, cursor: lockedFormats ? 'not-allowed' : 'pointer' }}>
            <input
              type="radio" name="plan-formats-mode" value="all"
              checked={formatsMode === 'all'} disabled={lockedFormats}
              onChange={() => setFormatsMode('all')}
            />
            {t('planModal.formats.modeAll')}
          </label>
          <label style={{ ...checkboxRow, marginBottom: '10px' }}>
            <input
              type="radio" name="plan-formats-mode" value="restricted"
              checked={formatsMode === 'restricted'}
              onChange={() => setFormatsMode('restricted')}
            />
            {t('planModal.formats.modeRestricted')}
          </label>
          {lockedFormats && <div style={noticeStyle}>{t('planModal.formats.lockedAll')}</div>}

          {formatsMode === 'restricted' && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                {formats.length === 0 && <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{t('planModal.formats.none')}</span>}
                {formats.map((token) => (
                  <span key={token} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '4px 8px', borderRadius: RADII.pill,
                    background: SEMANTIC.viola.bg, color: SEMANTIC.viola.fg,
                    fontSize: '12px', fontWeight: 600,
                  }}>
                    {token}
                    <button
                      type="button" aria-label={t('planModal.formats.removeToken', { token })}
                      onClick={() => setFormats((prev) => prev.filter((x) => x !== token))}
                      style={{ border: 'none', background: 'transparent', color: SEMANTIC.viola.fg, cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }}
                    >×</button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  id="plan-format-token" type="text" value={formatDraft}
                  onChange={e => setFormatDraft(e.target.value)} onKeyDown={handleFormatKeyDown}
                  placeholder={t('planModal.formats.placeholder')}
                  aria-label={t('planModal.formats.addLabel')} style={inputStyle}
                />
                <button
                  type="button" onClick={() => addFormatToken(formatDraft)}
                  style={{
                    padding: '10px 14px', borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`,
                    background: COLORS.surface, color: COLORS.ink, fontSize: '13px', fontWeight: 600,
                    cursor: 'pointer', fontFamily: FONT_SANS, whiteSpace: 'nowrap',
                  }}
                >{t('planModal.formats.add')}</button>
              </div>
              {formatError && (
                <div style={{ ...noticeStyle, background: SEMANTIC.errore.bg, color: SEMANTIC.errore.fg }}>{formatError}</div>
              )}
              <span style={helpStyle}>{t('planModal.formats.grammar')}</span>
              <span style={{ ...helpStyle, color: SEMANTIC.errore.fg }}>{t('planModal.formats.bareTrap')}</span>
            </div>
          )}
        </fieldset>

        {/* ── Caps: seats per workspace vs workspaces per licence ────── */}
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{t('planModal.maxUsers.legend')}</legend>
          <label htmlFor="plan-max-users" style={labelStyle}>{t('planModal.maxUsers.label')}</label>
          <input
            id="plan-max-users" type="number" min="1" step="1" value={maxUsers}
            onChange={e => setMaxUsers(e.target.value)}
            placeholder={t('planModal.maxUsers.placeholder')} style={inputStyle}
          />
          <span style={helpStyle}>{t('planModal.maxUsers.help')}</span>
          {lockedSeats && <div style={noticeStyle}>{t('planModal.notClearable', { field: t('planModal.maxUsers.label') })}</div>}
        </fieldset>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{t('planModal.maxTenants.legend')}</legend>
          <label htmlFor="plan-max-tenants" style={labelStyle}>{t('planModal.maxTenants.label')}</label>
          <input
            id="plan-max-tenants" type="number" min="1" step="1" value={maxTenants}
            onChange={e => setMaxTenants(e.target.value)}
            placeholder={t('planModal.maxTenants.placeholder')} style={inputStyle}
          />
          <span style={helpStyle}>{t('planModal.maxTenants.help')}</span>
          {lockedTenants && <div style={noticeStyle}>{t('planModal.notClearable', { field: t('planModal.maxTenants.label') })}</div>}
        </fieldset>

        {/* ── Distribution + status ─────────────────────────────────── */}
        <label style={{ ...checkboxRow, marginBottom: '4px' }}>
          <input type="checkbox" checked={selfServe} onChange={e => setSelfServe(e.target.checked)} />
          {t('planModal.selfServe.label')}
        </label>
        <span style={{ ...helpStyle, marginBottom: '16px' }}>{t('planModal.selfServe.help')}</span>

        {isEdit ? (
          <label style={{ ...checkboxRow, marginTop: '12px', marginBottom: '20px' }}>
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
            {t('planModal.planActive')}
          </label>
        ) : (
          <div style={{ ...noticeStyle, marginTop: '12px', marginBottom: '20px' }}>{t('planModal.activeOnCreate')}</div>
        )}

        {/* ── Legacy cost scale, folded away ────────────────────────── */}
        <div style={{ marginBottom: '20px' }}>
          <button
            type="button" onClick={() => setShowLegacy((v) => !v)}
            style={{ border: 'none', background: 'transparent', color: COLORS.textSecondary, fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: FONT_SANS, textDecoration: 'underline' }}
          >
            {showLegacy ? t('planModal.legacy.hide') : t('planModal.legacy.show')}
          </button>
          {showLegacy && (
            <div style={{ marginTop: '10px' }}>
              <label htmlFor="plan-credits" style={labelStyle}>{t('planModal.monthlyCredits')}</label>
              <input
                id="plan-credits" type="number" min="0" step="1" value={monthlyCredits}
                onChange={e => setMonthlyCredits(e.target.value)} style={inputStyle}
              />
              <span style={helpStyle}>{t('planModal.legacy.help')}</span>
            </div>
          )}
        </div>

        {error && (
          <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}`, color: SEMANTIC.errore.fg, fontSize: '13px' }}>
            {error}
          </div>
        )}

        <button type="submit" disabled={loading} style={{
          width: '100%', padding: '10px', borderRadius: RADII.controlInner, border: 'none',
          background: loading ? COLORS.borderStrong : COLORS.accent, color: COLORS.surface,
          fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
        }}>
          {loading ? t('planModal.saving') : (isEdit ? t('planModal.saveChanges') : t('planModal.create'))}
        </button>
      </form>
    </AdminModal>
  );
}
